import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';

import { AuthProvider } from '@/lib/auth';
import { MallProvider } from '@/lib/mall';
import { CartProvider } from '@/lib/cart';
import Layout from '@/components/Layout';

// Consumer pages
import Home from '@/pages/Home';
import Malls from '@/pages/Malls';
import MallHub from '@/pages/MallHub';
import Brands from '@/pages/Brands';
import BrandDetail from '@/pages/BrandDetail';
import Shop from '@/pages/Shop';
import ProductDetail from '@/pages/ProductDetail';
import Checkout from '@/pages/Checkout';
import Orders from '@/pages/Orders';
import OrderTracking from '@/pages/OrderTracking';
import Offers from '@/pages/Offers';
import Events from '@/pages/Events';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Loyalty from '@/pages/Loyalty';
import Bills from '@/pages/Bills';
import Rewards from '@/pages/Rewards';
import Games from '@/pages/Games';
import Concierge from '@/pages/Concierge';
import PlanMyVisit from '@/pages/PlanMyVisit';
import Wishlist from '@/pages/Wishlist';
import Compare from '@/pages/Compare';
import MallPass from '@/pages/MallPass';
import AddressBook from '@/pages/AddressBook';
import Returns from '@/pages/Returns';
import Profile from '@/pages/Profile';

// Admin
import AdminLogin from '@/pages/admin/AdminLogin';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminMalls from '@/pages/admin/AdminMalls';
import AdminBrands from '@/pages/admin/AdminBrands';
import AdminCustomers from '@/pages/admin/AdminCustomers';
import AdminBills from '@/pages/admin/AdminBills';
import AdminOffers from '@/pages/admin/AdminOffers';
import AdminEvents from '@/pages/admin/AdminEvents';
import AdminRewards from '@/pages/admin/AdminRewards';
import AdminAnalytics from '@/pages/admin/AdminAnalytics';
import AdminCatalog from '@/pages/admin/AdminCatalog';
import AdminProducts from '@/pages/admin/AdminProducts';
import AdminMarketing, { AdminMarketingNew, AdminMarketingDetail } from '@/pages/admin/AdminMarketing';
import FundleProposal from '@/pages/proposal/FundleProposal';

// Brand Portal (tenants)
import BrandLogin from '@/pages/brand/BrandLogin';
import BrandDashboard from '@/pages/brand/BrandDashboard';
import BrandOrders from '@/pages/brand/BrandOrders';
import BrandProducts from '@/pages/brand/BrandProducts';
import BrandInventory from '@/pages/brand/BrandInventory';
import BrandAnalytics from '@/pages/brand/BrandAnalytics';

function W({ children }) { return <Layout>{children}</Layout>; }

function App() {
    return (
        <AuthProvider>
            <MallProvider>
                <CartProvider>
                    <BrowserRouter>
                        <Toaster position="top-center" richColors />
                        <Routes>
                            {/* Standalone */}
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/proposal" element={<FundleProposal />} />

                            {/* Admin */}
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin" element={<AdminDashboard />} />
                            <Route path="/admin/malls" element={<AdminMalls />} />
                            <Route path="/admin/brands" element={<AdminBrands />} />
                            <Route path="/admin/customers" element={<AdminCustomers />} />
                            <Route path="/admin/bills" element={<AdminBills />} />
                            <Route path="/admin/offers" element={<AdminOffers />} />
                            <Route path="/admin/events" element={<AdminEvents />} />
                            <Route path="/admin/rewards" element={<AdminRewards />} />
                            <Route path="/admin/analytics" element={<AdminAnalytics />} />
                            <Route path="/admin/catalog" element={<AdminCatalog />} />
                            <Route path="/admin/products" element={<AdminProducts />} />
                            <Route path="/admin/marketing" element={<AdminMarketing />} />
                            <Route path="/admin/marketing/new" element={<AdminMarketingNew />} />
                            <Route path="/admin/marketing/:cid" element={<AdminMarketingDetail />} />

                            {/* Brand Portal */}
                            <Route path="/brand/login" element={<BrandLogin />} />
                            <Route path="/brand" element={<BrandDashboard />} />
                            <Route path="/brand/orders" element={<BrandOrders />} />
                            <Route path="/brand/products" element={<BrandProducts />} />
                            <Route path="/brand/inventory" element={<BrandInventory />} />
                            <Route path="/brand/analytics" element={<BrandAnalytics />} />

                            {/* Consumer */}
                            <Route path="/" element={<W><Home /></W>} />
                            <Route path="/malls" element={<W><Malls /></W>} />
                            <Route path="/mall/:slug" element={<W><MallHub /></W>} />
                            <Route path="/brands" element={<W><Brands /></W>} />
                            <Route path="/brands/:slug" element={<W><BrandDetail /></W>} />
                            <Route path="/shop" element={<W><Shop /></W>} />
                            <Route path="/product/:id" element={<W><ProductDetail /></W>} />
                            <Route path="/checkout" element={<W><Checkout /></W>} />
                            <Route path="/orders" element={<W><Orders /></W>} />
                            <Route path="/orders/:orderId/track" element={<W><OrderTracking /></W>} />
                            <Route path="/offers" element={<W><Offers /></W>} />
                            <Route path="/events" element={<W><Events /></W>} />
                            <Route path="/loyalty" element={<W><Loyalty /></W>} />
                            <Route path="/bills" element={<W><Bills /></W>} />
                            <Route path="/rewards" element={<W><Rewards /></W>} />
                            <Route path="/games" element={<W><Games /></W>} />
                            <Route path="/concierge" element={<W><Concierge /></W>} />
                            <Route path="/plan" element={<W><PlanMyVisit /></W>} />
                            <Route path="/wishlist" element={<W><Wishlist /></W>} />
                            <Route path="/compare" element={<W><Compare /></W>} />
                            <Route path="/mall-pass" element={<W><MallPass /></W>} />
                            <Route path="/account/addresses" element={<W><AddressBook /></W>} />
                            <Route path="/account/returns" element={<W><Returns /></W>} />
                            <Route path="/profile" element={<W><Profile /></W>} />
                            <Route path="/account" element={<W><Profile /></W>} />
                        </Routes>
                    </BrowserRouter>
                </CartProvider>
            </MallProvider>
        </AuthProvider>
    );
}

export default App;
